import React, { useState } from "react";
import {
    DndContext,
    DragEndEvent,
    DragOverlay,
    PointerSensor,
    useSensor,
    useSensors,
} from "@dnd-kit/core";

import TicketForm from "./TicketForm";
import type { Ticket } from "./types";
import "./Ticket.css";

import Modal from "./components/Modal";
import TicketsListPanel from "./components/TicketsListPanel";
import TargetsPanel from "./components/TargetsPanel";
import TicketDraggableCard from "./components/TicketDraggableCard";
import { useTicketsBoard } from "./hooks/useTicketsBoard";
import ToastHost from "./components/ToastHost";
import { useToasts } from "./hooks/useToasts";
import { useConfirm } from "./hooks/useConfirm";
import HeroSectionV2 from "../../../components/HeroSectionV2";
import Billede1 from "../../../Images/Billlede-1.png";

import { canAssignTicketToShift, getShiftTicketMismatchReason } from "./utils/ticketRules";

const TicketsBoardPage: React.FC = () => {
    const toast = useToasts();
    const { confirm, ConfirmUI } = useConfirm();

    const {
        filteredTickets,
        selectedTicket,
        setSelectedTicket,
        ticketSearch,
        setTicketSearch,
        showActiveOnly,
        setShowActiveOnly,
        hideExpired,
        setHideExpired,

        tab,
        setTab,

        personSearch,
        setPersonSearch,
        personChipsById,

        events,
        eventsLoading,
        selectedEventId,
        setSelectedEventId,
        shifts,
        shiftsLoading,
        refreshShifts,
        shiftChipsById,

        functionsLoading,
        functionsError,
        filteredFunctions,
        functionSearch,
        setFunctionSearch,
        functionChipsById,
        refreshFunctions,

        onCreateTicket,
        onSaveTicket,
        onDeleteTicket,
        assignTicketTo,
    } = useTicketsBoard({ confirm, toast });

    const [activeDragTicket, setActiveDragTicket] = useState<Ticket | null>(null);
    const [ignoreShiftTimeRule, setIgnoreShiftTimeRule] = useState(false);

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: { distance: 5 },
        })
    );

    const onDragStart = (event: any) => {
        const ticket: Ticket | undefined = event?.active?.data?.current?.ticket;
        setActiveDragTicket(ticket ?? null);
    };

    const onDragEnd = async (event: DragEndEvent) => {
        const ticket: Ticket | undefined = event.active.data.current?.ticket;
        setActiveDragTicket(null);

        if (!ticket?.ticketID) return;
        if (!event.over?.id) return;

        const overId = String(event.over.id);

        if (overId.startsWith("person-")) {
            await assignTicketTo(ticket.ticketID, "person", Number(overId.replace("person-", "")));
            return;
        }

        if (overId.startsWith("shift-")) {
            const shiftID = Number(overId.replace("shift-", ""));
            const shift = shifts.find((s) => s.shiftsID === shiftID);
            if (!shift) return;

            if (!ignoreShiftTimeRule && !canAssignTicketToShift(ticket, shift)) {
                const reason = getShiftTicketMismatchReason(ticket, shift) ?? "Ugyldig tildeling";
                toast.error(reason);
                return;
            }

            await assignTicketTo(ticket.ticketID, "shift", shiftID);
            return;
        }

        if (overId.startsWith("function-")) {
            await assignTicketTo(ticket.ticketID, "function", Number(overId.replace("function-", "")));
        }
    };

    return (
        <div>
            <HeroSectionV2
                titleEn="Ticket"
                titleDa="Billetter"
                descriptionEn="Create and assign tickets"
                descriptionDa="Lav og tildel billetter"
                imageSrc={Billede1}
            />

            <div style={{ display: "grid", gridTemplateColumns: "420px 1fr", gap: 16 }}>
                <DndContext sensors={sensors} onDragStart={onDragStart} onDragEnd={onDragEnd}>

                    <TicketsListPanel
                        tickets={filteredTickets}
                        selectedTicket={selectedTicket}
                        onSelectTicket={setSelectedTicket}
                        onCreate={onCreateTicket}
                        onDelete={onDeleteTicket}
                        onBulkCreate={() => { }}
                        ticketSearch={ticketSearch}
                        setTicketSearch={setTicketSearch}
                        showActiveOnly={showActiveOnly}
                        setShowActiveOnly={setShowActiveOnly}
                        hideExpired={hideExpired}
                        setHideExpired={setHideExpired}
                        ignoreShiftTimeRule={ignoreShiftTimeRule}
                        setIgnoreShiftTimeRule={setIgnoreShiftTimeRule}
                    />

                    <TargetsPanel
                        tab={tab}
                        setTab={setTab}
                        activeTicket={activeDragTicket ?? selectedTicket ?? null}
                        ignoreShiftTimeRule={ignoreShiftTimeRule}
                        personSearch={personSearch}
                        setPersonSearch={setPersonSearch}
                        personChipsById={personChipsById}
                        events={events}
                        eventsLoading={eventsLoading}
                        selectedEventId={selectedEventId}
                        setSelectedEventId={setSelectedEventId}
                        shifts={shifts}
                        shiftsLoading={shiftsLoading}
                        refreshShifts={refreshShifts}
                        shiftChipsById={shiftChipsById}
                        functionsLoading={functionsLoading}
                        functionsError={functionsError}
                        refreshFunctions={refreshFunctions}
                        functionSearch={functionSearch}
                        setFunctionSearch={setFunctionSearch}
                        functions={filteredFunctions}
                        functionChipsById={functionChipsById}
                    />

                    <DragOverlay>
                        {activeDragTicket && (
                            <TicketDraggableCard ticket={activeDragTicket} selected={false} onSelect={() => { }} />
                        )}
                    </DragOverlay>

                    {ConfirmUI}
                    <ToastHost items={toast.toasts} onDismiss={toast.dismiss} />
                </DndContext>
            </div>
        </div>
    );
};

export default TicketsBoardPage;